import React, { useState, useEffect } from 'react';
import { UserCircleDisplay, getInitials, getRandomColor } from '../../utils/UserCustomDisplay';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import PaginationControls from '../../utils/PaginationControls';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import { useTimezone } from '../../utils/TimezoneContext';

const TeamList = ({ 
  teams,
  total,
  page,
  setPage,
  size,
  setSize,
  search,
  setSearch,
  filterType,
  setFilterType,
  onRefresh,
  onEditTeam
}) => {
  // Delete team states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [memberDetails, setMemberDetails] = useState({});
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  
  // Search input state (separate from the actual search term)
  const [searchInput, setSearchInput] = useState(search);
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();

  // API base URL
  const API_BASE_URL = '/pceapi/teams';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (team) => {
    setTeamToDelete(team);
    setShowDeleteConfirm(true);
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  // Close success popup
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    onRefresh();
  };
  
  // Toggle team details expansion
  const toggleTeamExpansion = async (teamId) => {
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null);
      return;
    }
    
    setExpandedTeamId(teamId);
    
    // Only fetch members if we don't already have them cached
    if (!memberDetails[teamId]) {
      await fetchTeamMembers(teamId);
    }
  };
  
  // Fetch team members for expanded view
  const fetchTeamMembers = async (teamId) => {
    setIsLoadingMembers(true);
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/details/${teamId}`,
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'failed' || data.status === 'NotAuthorized') {
        setIsLoadingMembers(false);
        return;
      }
      
      setMemberDetails(prev => ({
        ...prev,
        [teamId]: data.data.members || []
      }));
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };
  
  // Handle team deletion
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (!teamToDelete) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting team "${teamToDelete.name}"`);
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete-team?team_id=${teamToDelete.id}`,
        { 
          headers
        }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to delete team');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(data.message || 'Team deleted successfully');
      setIsSuccessPopupVisible(true);
      
    } catch (error) {
      console.error('Error deleting team:', error);
      setPopupErrorMessage(error.message || 'Failed to delete team');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setTeamToDelete(null);
    }
  };
  
  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Generate body content for delete modal
  const getDeleteModalBodyContent = () => {
    if (!teamToDelete) return null;
    
    return (
      <div>
        <p className="mb-0">This will permanently delete:</p>
        <ul>
          <li>The team entry</li>
          <li>All team membership records</li>
          <li>Associated access permissions</li>
        </ul>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };

  return (
    <div className="ev-card-4 w-100 ev-padding-large">
      <div className="row mt-1 mb-0">
        <div className="col-5 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search by team name"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        
        <div className="col-7 text-end">
          <div className="btn-group custom-btn-group-outer">
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'ALL' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('ALL')}
            >
              All
            </button>
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'PUBLIC' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('PUBLIC')}
            >
              Public
            </button>
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'PRIVATE' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('PRIVATE')}
            >
              Private
            </button>
          </div>
          <button 
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
            onClick={onRefresh}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh 
          </button>
        </div>
      </div>
      
      <div className="table-container">
        <table className="table-custom1 mt-0 overflow-hidden w-100">
          <thead>
            <tr>
              <th className="w-25">
                <label>Team Name</label>
              </th>
              <th className="w-20 text-center">
                <label>Owner</label>
              </th>
              <th className="w-10 text-center">
                <label>Access</label>
              </th>
              <th className="w-20 text-center">
                <label>Team Members</label>
              </th>
              <th className="w-15 text-center">
                <label>Created</label>
              </th>
              <th className="w-10">
                <label>Actions</label>
              </th>
            </tr>
          </thead>
        </table>

        <div className="scrollable-element dashboard-right-contentarea-table-height-1">
          <table className="table-custom1 overflow-hidden w-100">
            <tbody>
              {teams.length === 0 ? (
                <div className="text-center py-5">
                  <p className="ev-small-p-blue">No teams found</p>
                </div>
              ) : (
                teams.map((team) => (
                  <React.Fragment key={team.id}>
                    <tr 
                      className={expandedTeamId === team.id ? "selected-row" : ""}
                    >
                      <td className="align-top w-25" onClick={() => toggleTeamExpansion(team.id)}>
                        <div className="ev-reg-p-blue ev-bold-large mb-0 avatar-tooltip" data-tooltip={team.name || 'None'}>
                          <TrimContent text={`${team.name} (${team.member_count})`} maxLength={25} />      
                        </div>
                      </td>
                      <td className="align-top w-20 text-center avatar-tooltip" data-tooltip={team.created_by_email || 'None'} onClick={() => toggleTeamExpansion(team.id)}>
                        <div className="ev-message-card5 ev-fs-11 d-inline-block">
                          <TrimContent text={team.created_by_email} maxLength={20} />
                        </div>
                      </td>
                      <td className="align-top w-10 text-center" onClick={() => toggleTeamExpansion(team.id)}>
                        <span className={`badge badge-sm ${team.team_access_level === 'PUBLIC' ? 'bg-primary w-100' : 'bg-danger w-100'}`}>
                          {team.team_access_level}
                        </span>
                      </td>
                      <td className="align-top w-20" onClick={() => toggleTeamExpansion(team.id)}>
                        <div className="d-flex justify-content-center">
                          <UserCircleDisplay users={team.user_access} maxDisplay={4} />
                        </div>
                      </td>
                      <td className="align-top w-15 text-center" onClick={() => toggleTeamExpansion(team.id)}>{formatDateTime(team.created_at)}</td>
                      <td className="align-top w-10">
                        <div className="btn-group" role="group">
                          <button 
                            className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(team);
                            }}
                            data-tooltip="Delete Team"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                          <button 
                            className="btn ev-btn-skyblue-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditTeam(team);
                            }}
                            data-tooltip="Edit Team"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="ev-small-p-dark">
          Showing {teams.length} of {total} entries
        </div>
        <div className="d-flex gap-2">
          <button 
            className="ev-btn-light-fill" 
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i> 
          </button>
          <button className="ev-btn-light-fill" disabled>
            Page {page} of {Math.max(1, Math.ceil(total / size))}
          </button>
          <button 
            className="ev-btn-light-fill" 
            disabled={page >= Math.ceil(total / size) || total === 0}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
          <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
        </div>
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        objectName="team"
        itemName={teamToDelete?.name}
        bodyContent={getDeleteModalBodyContent()}
      />
    </div>
  );
};

export default TeamList;